import React, { createContext, useCallback, useContext, useState } from "react";

export interface ConfirmationOptions {
    message: React.ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    confirmColor?: string;
}

export interface InfoDialogOptions {
    message: React.ReactNode;
    closeLabel?: string;
}

export interface ConfirmationState extends ConfirmationOptions {
    variant: "confirmation";
    resolve: (value: boolean) => void;
}

export interface InfoDialogState extends InfoDialogOptions {
    variant: "info";
    resolve: () => void;
}

export type DialogState = ConfirmationState | InfoDialogState;

interface DialogContextValue {
    confirm: (options: ConfirmationOptions) => Promise<boolean>;
    showInfo: (options: InfoDialogOptions) => Promise<void>;
    pending: DialogState | null;
    handleConfirm: () => void;
    handleCancel: () => void;
    handleClose: () => void;
}

const DialogContext = createContext<DialogContextValue | undefined>(undefined);

export function DialogProvider({ children }: { children: React.ReactNode }) {
    const [pending, setPending] = useState<DialogState | null>(null);

    const confirm = useCallback((options: ConfirmationOptions): Promise<boolean> => {
        return new Promise((resolve) => {
            setPending({ variant: "confirmation", ...options, resolve });
        });
    }, []);

    const showInfo = useCallback((options: InfoDialogOptions): Promise<void> => {
        return new Promise((resolve) => {
            setPending({ variant: "info", ...options, resolve });
        });
    }, []);

    const handleConfirm = useCallback(() => {
        if (pending?.variant !== "confirmation") {
            return;
        }

        pending.resolve(true);
        setPending(null);
    }, [pending]);

    const handleCancel = useCallback(() => {
        if (pending?.variant !== "confirmation") {
            return;
        }

        pending.resolve(false);
        setPending(null);
    }, [pending]);

    const handleClose = useCallback(() => {
        if (pending?.variant !== "info") {
            return;
        }

        pending.resolve();
        setPending(null);
    }, [pending]);

    return (
        <DialogContext.Provider value={{ confirm, showInfo, pending, handleConfirm, handleCancel, handleClose }}>
            {children}
        </DialogContext.Provider>
    );
}

export function useDialog() {
    const ctx = useContext(DialogContext);
    if (!ctx) {
        throw new Error("Dialog hooks must be used within DialogProvider");
    }

    return ctx;
}